import React from 'react';
import { compTakesTeamEntries } from '../utils/competition';

/* ====================================================================
   MY CLUB TEAMS — offered first when entering a team competition
   ====================================================================
   A player entering a triples competition who is already in a triples
   team at the club picks that team rather than typing it in again. Used
   by both registration forms (the QR/link flow and the button on the
   competition home page). */

// Players per side in each club team discipline, as on the server's ClubTeam.
const CLUB_TEAM_SIZE = { pairs: 2, triples: 3, fours: 4 };

const isActive = u => !!(u.ClubTeamPlayer && u.ClubTeamPlayer.active);

/** The player's own club teams of this competition's size. */
export function useMyClubTeams(API, comp, userId) {
  const [clubTeams, setClubTeams] = React.useState([]);
  const takesTeams = !!comp && compTakesTeamEntries(comp);
  const clubId = comp && comp.ClubId;
  React.useEffect(() => {
    if (!takesTeams) return;
    fetch(`${API}/api/clubteams${clubId ? `?clubId=${clubId}` : ''}`)
      .then(r => (r.ok ? r.json() : []))
      .then(ts => setClubTeams(Array.isArray(ts) ? ts : []))
      .catch(() => {});
  }, [API, takesTeams, clubId]);
  if (!takesTeams) return [];
  return clubTeams.filter(ct => CLUB_TEAM_SIZE[ct.discipline] === comp.playersPerTeam
    && (ct.Users || []).some(u => u.id === userId));
}

/* The entry a club team makes: its name, and its playing line-up less the
   player themselves. A reserve entering takes the place of the last named
   player, which they can change before submitting. */
export function clubTeamEntry(ct, comp, userId, clubPlayers) {
  const needed = Math.max(0, (comp.playersPerTeam || 1) - 1);
  const others = (ct.Users || [])
    .filter(u => u.id !== userId)
    .sort((a, b) => Number(isActive(b)) - Number(isActive(a)))
    .slice(0, needed)
    .map(u => {
      const known = clubPlayers.find(p => p.id === u.id);
      return { id: u.id, name: u.name, email: (known && known.email) || u.email || '' };
    });
  return {
    teamName: ct.name,
    teamMembers: others.length ? others : [{ id: '', name: '', email: '' }],
  };
}

function MyClubTeamPicker({ teams, onPick, onNew }) {
  return (
    <div className="w3-margin-bottom">
      <label className="w3-small w3-text-grey">Which team are you entering?</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
        {teams.map(ct => (
          <button key={ct.id} className="w3-button w3-white w3-border w3-round"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', whiteSpace: 'normal', padding: '10px 14px' }}
            onClick={() => onPick(ct)}>
            <strong>{ct.name}</strong>
            <div className="w3-small w3-text-grey">
              {(ct.Users || []).filter(isActive).map(u => u.name).join(', ') || 'No players listed'}
            </div>
          </button>
        ))}
        <button className="w3-button w3-theme-l4 w3-round" style={{ textAlign: 'left', padding: '10px 14px' }} onClick={onNew}>
          <i className="fa fa-plus w3-margin-right"></i>Enter a different team
        </button>
      </div>
    </div>
  );
}

export default MyClubTeamPicker;
